/* eslint-disable */

import React, { useCallback, useState } from 'react';
import { Stack, Typography, IconButton, Divider, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AgGridReact } from 'ag-grid-react';
import { Icon } from '@iconify/react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '../../components';
import FilterSaveOrEditDialog from './FilterSaveOrEditDialog';
import { saveFilter, updateFilter } from '../../services/vehicles';
import { ISavedFilter } from '../../types';
import { RootState } from '../../redux/store';
import { refreshSavedFilters, setColumns, pushAppliedFilter } from '../../redux/slices/filterSlice';

interface FilterDisplayState {
  id: string;
  filter: string;
  value: string;
}

interface IProps {
  filterModel: { [key: string]: any };
  gridRef?: React.RefObject<AgGridReact>;
  columnDefs: any;
}

const AppliedFilters: React.FC<IProps> = ({ filterModel, gridRef, columnDefs }) => {
  const appliedFilter = useSelector((state: RootState) => state.filter.appliedFilter);
  const [isOpenFilterDialogOpen, setIsOpenFilterDialogOpen] = useState(false);
  const [savedFilter, setSavedFilter] = useState<Partial<ISavedFilter>>({});
  const [isEdit, setIsEdit] = useState(false);
  const savedFiltersRefesher = useSelector((state: RootState) => state.filter.savedFiltersRefesher);
  const dispatch = useDispatch();

  const removeFilter = useCallback(
    (filter: string) => {
      if (gridRef?.current?.api.setFilterModel) {
        const temp = { ...filterModel };
        delete temp[filter];
        gridRef?.current?.api.setFilterModel(temp);
      }
    },
    [filterModel, gridRef]
  );

  const clearAll = useCallback(() => {
    dispatch(setColumns(columnDefs.map((column: any) => column.field)));
    if (gridRef?.current?.api.setFilterModel) gridRef?.current?.api.setFilterModel(undefined);
  }, [columnDefs, dispatch, gridRef]);

  const onSaveFilterDialogClose = useCallback(() => {
    setIsOpenFilterDialogOpen(false);
    // clearAll();
  }, []);

  const handleFilterSave = useCallback(
    (name: string, isDefault: boolean, isPublic: boolean, id?: string) => {
      if (id) {
        updateFilter(id, {
          name,
          isDefault,
          isPublic,
          filters: filterModel,
          columns:
            gridRef?.current?.columnApi?.getAllDisplayedColumns().map((col) => col.getColDef().field || '') || [],
        }).then((response) => {
          setIsOpenFilterDialogOpen(false);
          dispatch(refreshSavedFilters(!savedFiltersRefesher));
          dispatch(pushAppliedFilter(response));
        });
      } else {
        saveFilter({
          name,
          isDefault,
          isPublic,
          filters: filterModel,
          columns:
            gridRef?.current?.columnApi?.getAllDisplayedColumns().map((col) => col.getColDef().field || '') || [],
        }).then((response) => {
          setIsOpenFilterDialogOpen(false);
          dispatch(refreshSavedFilters(!savedFiltersRefesher));
          // dispatch(pushAppliedFilter(response));
        });
      }
    },
    [filterModel, gridRef, dispatch, savedFiltersRefesher]
  );

  const assignmentNesting = (filter: string) => {
    if (filter.includes('prevAssgnChange.')) {
      return columnDefs[columnDefs.findIndex((col: any) => col.field === filter)].headerName;
    } else {
      return columnDefs.map(
        (column: any) => column?.children?.[column?.children?.findIndex((col: any) => col.field === filter)]?.headerName
      );
    }
  };

  const repl = (s: any) => {
    return s.toString().replace(/\d+/g, function (m: any) {
      return '000000000'.substring(m.length - 1) + m;
    });
  };

  const filters: FilterDisplayState[] = Object.keys(filterModel).map((filter: string) => ({
    id: filter,
    filter: filter.includes('.')
      ? assignmentNesting(filter)
      : columnDefs[columnDefs.findIndex((col: any) => col.field === filter)].headerName,
    value:
      filter === 'batchNumber'
        ? `${repl(filterModel?.[filter]?.filter)} - ${repl(filterModel?.[filter]?.filterTo)}`
        : filterModel?.[filter]?.values?.filter((v: any) => !!v)?.join(', ') ||
          filterModel?.[filter]?.filter ||
          (filterModel?.[filter]?.condition2?.filter
            ? `${filterModel?.[filter]?.condition2?.filter}, ${filterModel?.[filter]?.condition1?.filter}`
            : filterModel?.[filter]?.filter),
  }));

  return (
    <>
      <FilterSaveOrEditDialog
        open={isOpenFilterDialogOpen}
        onClose={onSaveFilterDialogClose}
        handleFilterSave={handleFilterSave}
        filter={savedFilter}
        setFilter={setSavedFilter}
        isEdit={isEdit}
      />
      <Stack
        direction="row"
        display="flex"
        justifyContent="space-between"
        p={1}
        sx={{ borderWidth: '1px 0px', borderStyle: 'solid', borderColor: '#E7E7E4' }}
        alignItems="center"
      >
        <Stack direction="row" gap={2} height="fit-content" alignItems="center">
          <Typography variant="subtitle2" lineHeight="120%" height="fit-content">
            Applied Filters
          </Typography>
          {filters.map(({ id, filter, value }) => (
            <Stack
              key={id}
              direction="row"
              alignItems="center"
              gap={1}
              borderRadius={1}
              pl={1}
              py={0.2}
              pr={0.2}
              bgcolor="rgba(69, 107, 217, 0.1)"
            >
              <Typography variant="caption" fontWeight={500} lineHeight="120%" height="fit-content">
                {filter}
              </Typography>
              <Typography variant="caption" fontWeight={500} lineHeight="120%" height="fit-content">
                |
              </Typography>
              <Typography variant="caption" lineHeight="120%" height="fit-content">
                {value}
              </Typography>
              <IconButton size="small" onClick={() => removeFilter(id)}>
                <Icon icon="eva:close-outline" fontSize={10} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          height="fit-content"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Button
            variant="text"
            disableRipple
            size="small"
            onClick={() => {
              setSavedFilter({});
              setIsEdit(false);
              setIsOpenFilterDialogOpen(true);
            }}
          >
            Create New Filter
          </Button>
          {appliedFilter && (
            <Button
              variant="text"
              disableRipple
              size="small"
              onClick={() => {
                setSavedFilter({ ...appliedFilter });
                setIsEdit(true);
                setIsOpenFilterDialogOpen(true);
              }}
            >
              Update Filter
            </Button>
          )}
          <Button onClick={clearAll} variant="text" disableRipple size="small">
            Clear All
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export default AppliedFilters;
